import React, { useEffect, useState } from 'react';
import { Radio, Loader2, Check, Zap } from 'lucide-react';
import {
  getConversationAutonomy,
  setConversationAutonomy,
  runConversationBeat,
  type ConversationAutonomy,
  type ProactiveLevel,
} from '../../services/conversationService';
import type { ChatSession } from './types';
import { TYPE } from '../../constants/typeScale';

/**
 * Owner control for proactive autonomy (group setting) — one simple dial driving BOTH the free ambient news
 * cadence and the bounded, charged agent-to-agent discussion. Mirrors the picker shown at group creation.
 * Saving is immediate.
 */
const LEVELS: { v: ProactiveLevel; label: string; hint: string }[] = [
  { v: 'none', label: 'None', hint: 'A quiet room. Agents stay silent until you message them and never start a thread on their own — no background news, no cross-talk, no credits spent.' },
  { v: 'occasional', label: 'Occasional', hint: 'A gentle pulse. Once in a while an agent drops a genuinely relevant headline or thought to keep things fresh, but they will not chat among themselves. Always free.' },
  { v: 'collaborative', label: 'Collaborative', hint: 'A working team. Agents bring up news, build on each other, and debate ideas between your messages — bounded by a small daily credit budget so it never runs away.' },
  { v: 'vibrant', label: 'Vibrant', hint: 'A buzzing group. Agents proactively discuss, react to each other, and track developments throughout the day for a lively, always-on feel. Uses the most credits (still capped daily).' },
];

/**
 * Which levels are OFFERED. Collaborative and Vibrant are withheld for now (owner decision 2026-08-13):
 * both spend credits on agents talking to each other, which is not what people come here to pay for.
 *
 * They stay in the type and in LEVELS on purpose — a group already set to one must still render its own
 * setting correctly and be able to move off it. We hide the door IN, not the door OUT.
 */
const OFFERED: ProactiveLevel[] = ['none', 'occasional'];

const AutonomySettings: React.FC<{ session: ChatSession }> = ({ session }) => {
  const [cfg, setCfg] = useState<ConversationAutonomy | null>(null);
  const [saving, setSaving] = useState(false);
  const [sparking, setSparking] = useState(false);
  const [sparkMsg, setSparkMsg] = useState('');

  const spark = async () => {
    setSparking(true); setSparkMsg('');
    try {
      const r = await runConversationBeat(session.id);
      setSparkMsg(r.emitted > 0 ? `${r.emitted} agent${r.emitted > 1 ? 's' : ''} chimed in` : 'Nothing to add just now (cooldown or budget) — try again shortly.');
    } catch { setSparkMsg('Could not spark a beat.'); }
    finally { setSparking(false); setTimeout(() => setSparkMsg(''), 5000); }
  };

  useEffect(() => {
    let alive = true;
    getConversationAutonomy(session.id).then((c) => { if (alive) setCfg(c); }).catch(() => undefined);
    return () => { alive = false; };
  }, [session.id]);

  const save = async (level: ProactiveLevel) => {
    setCfg((prev) => (prev ? { ...prev, level } : prev));
    setSaving(true);
    try { const next = await setConversationAutonomy(session.id, { level }); setCfg(next); }
    catch { /* keep optimistic value */ }
    finally { setSaving(false); }
  };

  if (!cfg) return null;
  // Legacy chats predate the dial — infer a level from the underlying knobs so the UI shows a selection.
  const current: ProactiveLevel = cfg.level
    || (cfg.discussion === 'active' ? 'vibrant'
      : cfg.discussion === 'lively' ? 'collaborative'
        : cfg.ambient !== 'off' ? 'occasional' : 'none');
  // Show the offered levels, plus whatever this group is actually on (so a withheld level is never silently
  // misrepresented as something else, and the owner can always see and change what they are running).
  const choices = LEVELS.filter((o) => OFFERED.includes(o.v) || o.v === current);

  return (
    <section>
      <p className={`mb-2 flex items-center gap-1.5 ${TYPE.label} text-secondary-400`}>
        <Radio className="w-3.5 h-3.5" /> Proactive agents
        {saving && <Loader2 className="w-3 h-3 animate-spin" />}
      </p>

      <div className="space-y-2">
        {choices.map((o) => (
          <button key={o.v} onClick={() => save(o.v)}
            className={`w-full text-left rounded-xl border p-3 transition-colors ${current === o.v
              ? 'border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-800'
              : 'border-secondary-200 dark:border-secondary-600 hover:border-secondary-400 dark:hover:border-secondary-500'}`}>
            <div className="flex items-center justify-between">
              <span className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>{o.label}</span>
              {current === o.v && <Check className="w-4 h-4 text-secondary-900 dark:text-white" />}
            </div>
            <p className={`mt-0.5 ${TYPE.meta} text-secondary-400 leading-snug`}>{o.hint}</p>
          </button>
        ))}
      </div>

      {current !== 'none' && (
        <div className="mt-3">
          <button onClick={spark} disabled={sparking}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-600 px-3 py-1.5 ${TYPE.control} font-medium text-secondary-700 dark:text-secondary-200 hover:border-secondary-400 disabled:opacity-50`}>
            {sparking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Spark a beat now
          </button>
          {sparkMsg && <p className={`mt-1 ${TYPE.meta} text-secondary-400`}>{sparkMsg}</p>}
        </div>
      )}
    </section>
  );
};

export default AutonomySettings;
